import { useEffect } from "react";

const SITE_NAME = "WorldAIInfo";
const DEFAULT_IMAGE = "/og-image.png";
const DEFAULT_TITLE = "WorldAIInfo.com — Global AI Tools, Jobs, Events & News";

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Per-page SEO. Imperatively updates head tags (no duplicates, no extra dependency).
 * Google renders JS and reads these; browser tab + canonical also update.
 */
export default function Seo({ title, description, image, type = "website", canonicalPath, jsonLd, ogTag }) {
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";
  useEffect(() => {
    // Canonical consistency: force apex worldaiinfo.com -> www.worldaiinfo.com in production.
    let origin = window.location.origin;
    try {
      const u = new URL(origin);
      if (u.hostname === "worldaiinfo.com") { u.hostname = "www.worldaiinfo.com"; origin = u.origin; }
    } catch (e) { /* keep origin */ }
    const path = canonicalPath ?? window.location.pathname;
    const url = origin + path;
    const fullTitle = title || DEFAULT_TITLE;
    // Auto-generate a branded OG image from the page title/description unless an explicit image is given.
    const params = new URLSearchParams({ title: fullTitle });
    if (description) params.set("subtitle", description);
    if (ogTag) params.set("tag", ogTag);
    const dynamicOg = `${origin}/api/og-image.png?${params.toString()}`;
    const img = image ? (image.startsWith("http") ? image : origin + image) : dynamicOg;

    document.title = fullTitle;
    upsertMeta("name", "description", description);
    upsertLink("canonical", url);

    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", img);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", img);

    let script;
    if (jsonLdKey) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.text = jsonLdKey;
      document.head.appendChild(script);
    }
    return () => { if (script) script.remove(); };
  }, [title, description, image, type, canonicalPath, jsonLdKey]);

  return null;
}
